import { Link } from '@tanstack/react-router'
import { ArrowUpRight, Package, ShoppingBasket } from 'lucide-react'
import { useProductSuggestions } from '@/api/hooks'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { formatMoney } from '@/lib/utils'

const reasonLabels = {
  accessory: 'Accessory',
  alternative: 'Alternative',
  same_product_type: 'Similar product',
} as const

export function ProductRecommendationShowcase({
  productId,
}: {
  productId: string
}) {
  const { data, isLoading, isError } = useProductSuggestions(productId)
  const suggestions = data?.suggestions ?? []

  return (
    <section aria-labelledby="product-suggestions-heading" data-product-suggestions="true">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <ShoppingBasket className="size-4 text-muted-foreground" aria-hidden="true" />
            <h2 id="product-suggestions-heading" className="text-base font-semibold">
              Suggested with this product
            </h2>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Live preview using this product as a one-item Shopify cart.
          </p>
        </div>
        <Badge variant="outline" className="font-normal">
          Recommendation API
        </Badge>
      </div>

      {isLoading ? (
        <div
          className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
          aria-label="Loading suggestions"
          role="status"
        >
          {Array.from({ length: 3 }, (_, index) => (
            <div key={index} className="flex min-h-32 gap-3 rounded-xl border bg-card p-4">
              <Skeleton className="size-20 shrink-0 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-20" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <div
          className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm"
          role="alert"
        >
          <p className="font-medium text-destructive">Suggestions are temporarily unavailable</p>
          <p className="mt-0.5 text-muted-foreground">
            Product details remain available while the recommendation request recovers.
          </p>
        </div>
      ) : suggestions.length === 0 ? (
        <div className="flex items-center gap-3 rounded-xl border border-dashed px-4 py-5">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted">
            <Package className="size-4 text-muted-foreground" aria-hidden="true" />
          </div>
          <div>
            <p className="text-sm font-medium">No eligible suggestions</p>
            <p className="text-xs text-muted-foreground">
              No CONFIG V4 links or same-type products are currently available.
            </p>
          </div>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {suggestions.map((suggestion) => {
            const product = suggestion.product
            const thumbnail = product.images
              .filter((image) => !image.hidden)
              .sort((left, right) => left.order - right.order)[0]
            const productTitle =
              product.store_title ?? product.seo_description ?? `${product.manufacturer_name} ${product.model}`
            return (
              <Link
                key={`${suggestion.reason}-${product.uuid}`}
                to="/products/$productId"
                params={{ productId: product.uuid }}
                className="group flex min-w-0 gap-3 rounded-xl border bg-card p-4 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {thumbnail ? (
                  <img
                    src={thumbnail.public_url ?? thumbnail.store_url ?? thumbnail.source_url}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="size-20 shrink-0 rounded-lg border bg-white object-contain"
                  />
                ) : (
                  <div className="flex size-20 shrink-0 items-center justify-center rounded-lg border bg-muted">
                    <Package className="size-6 text-muted-foreground/50" aria-hidden="true" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <Badge variant="secondary" className="max-w-full font-normal">
                      {reasonLabels[suggestion.reason]}
                    </Badge>
                    <ArrowUpRight
                      className="size-4 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground"
                      aria-hidden="true"
                    />
                  </div>
                  <p className="mt-2 break-words text-sm font-semibold text-primary group-hover:underline">
                    {product.model}
                  </p>
                  <p className="line-clamp-2 text-xs text-muted-foreground">{productTitle}</p>
                  <div className="mt-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                    <span className="text-xs text-muted-foreground">{product.manufacturer_name}</span>
                    <span className="text-sm font-semibold tabular-nums">
                      {formatMoney(product.current_price?.store_price)}
                    </span>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      )}
    </section>
  )
}
